import {SearchRequest} from '@elastic/elasticsearch/lib/api/types';
import {QueryFunction, QueryKey, UseQueryOptions, useQuery} from '@tanstack/react-query';
import {ElasticDatabase, ElasticItem} from '@tnc/shared';

import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';

/*******************************************************************
 * Types
 *******************************************************************/
type UseElasticQueryResponseT = {
  data: Array<{
    _id: string;
    _source: any;
    // Add other Elasticsearch hit fields as needed
  }>;
  total: number;
  currentPage: number;
  totalPages: number;
  // Add aggregations to the response type
  aggregations?: Record<string, any>;
};

type FetchElasticDataPropsT = {
  searchRequest: SearchRequest;
  page?: number | null;
  limit?: number | null;
  searchColumn?: string | null;
  searchTerm?: string | null;
  // Add aggs parameter
  aggs?: Record<string, any> | null;
};

type UseElasticQueryT<T extends keyof ElasticDatabase> = {
  table: T;
  searchRequest: Omit<SearchRequest, '_source'> & {
    _source?: Array<keyof ElasticItem<T>>;
  };
  enabled?: boolean;
  protect?: boolean;
  authOverride?: boolean;
  page?: number | null;
  limit?: number | null;
  searchColumn?: string | null;
  searchTerm?: string | null;
  // Add aggs parameter
  aggs?: Record<string, any> | null;
} & Omit<UseQueryOptions<UseElasticQueryResponseT, Error, UseElasticQueryResponseT, QueryKey>, 'queryKey' | 'queryFn'>;

/*********************************************************************
 * Helper Functions
 *********************************************************************/
async function fetchElasticData({searchRequest, page = null, limit = null, aggs = null}: FetchElasticDataPropsT) {
  // Rule: Code Style - Use descriptive variable names with auxiliary verbs
  const effectiveLimit = limit ? limit : (searchRequest.size ?? 10);
  const from = page ? (page - 1) * effectiveLimit : 0;

  // Ensure we have a valid index
  if (!searchRequest.index) {
    console.error('Index is required for Elasticsearch query', searchRequest);
    throw new Error('Index is required for Elasticsearch query');
  }

  const {index, ...restSearchRequest} = searchRequest;

  // console.log(
  //   'SEARCH REQUEST BEING SENT TO ELASTICSEARCH',
  //   JSON.stringify(
  //     {
  //       ...restSearchRequest,
  //       from,
  //       size: effectiveLimit,
  //       aggs: aggs ?? {},
  //     },
  //     null,
  //     2
  //   )
  // );

  // Rule: Code Style - Use functional programming patterns
  const {data, error} = await SupabaseInstance.supabase.functions.invoke('elasticsearch-api', {
    body: {
      index,
      requestBody: {
        ...restSearchRequest,
        from,
        size: effectiveLimit,
        aggs: aggs ?? {},
      },
    },
  });

  if (error) {
    console.error('Error invoking Elasticsearch API:', error);
    throw new Error(error.message || 'Unknown error');
  }

  return {
    data: data.hits.hits,
    total: data.hits.total.value,
    currentPage: page ?? 1,
    totalPages: Math.ceil((data.hits.total.value ?? 0) / (limit ?? 10)),
    aggregations: data.aggregations,
  };
}

/*********************************************************************
 * useElasticQuery Hook
 * Wrapper around useQuery to fetch data from Elasticsearch
 *********************************************************************/
export function useElasticQuery<T extends keyof ElasticDatabase>({
  table,
  searchRequest,
  enabled: _enabled = true,
  protect,
  authOverride = false,
  page = null,
  limit = null,
  searchColumn = null,
  searchTerm = null,
  aggs = null,
  ...rest
}: UseElasticQueryT<T>) {
  const {isAdministrator, isAuthenticated} = useAuth();
  const enabled = (protect ? isAdministrator && _enabled : _enabled) && (authOverride || isAuthenticated);

  const queryKey: QueryKey = ['elasticsearchData', table, searchRequest, page, limit, aggs];

  const queryFn: QueryFunction<UseElasticQueryResponseT, QueryKey> = async () => {
    return fetchElasticData({
      searchRequest: searchRequest as SearchRequest,
      page,
      limit,
      searchColumn,
      searchTerm,
      aggs: aggs ?? {},
    });
  };

  const queryOptions: UseQueryOptions<UseElasticQueryResponseT, Error, UseElasticQueryResponseT, QueryKey> = {
    enabled,
    ...rest,
    queryKey,
    queryFn,
  };

  const queryResult = useQuery(queryOptions);

  return {
    ...queryResult,
    pagination: {
      currentPage: page ?? 1,
      totalPages: Math.ceil((queryResult.data?.total ?? 0) / (limit ?? 10)),
      totalItems: queryResult.data?.total ?? 0,
    },
  };
}
